import type { CityConfig } from "@/pages/city-template/types";

export const conwayNhConfig: CityConfig = {
  city: "Conway",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "conway-nh",
  route: "/cities/conway-nh",
  title: "Emergency & Expedited Passport Services Conway NH | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Conway, NH — as fast as 24 hours. No Conway passport agency. Fast Passport Center couriers to the Boston Passport Agency, about 130 miles south. A+ BBB rated. Serving White Mountains residents, Conway Scenic Railroad travelers, and Carroll County families heading abroad for vacation, study, or business.",
  h1: "Emergency & Expedited Passport Services in Conway, NH",
  heroSubtitle: "Conway sits at the gateway to the White Mountain National Forest in Carroll County, home to the Conway Scenic Railroad, outlet shopping, and year-round outdoor recreation. Fast Passport Center's registered couriers deliver your documents to the Boston Passport Agency so you get a new or renewed passport as fast as 24 hours — whether you're a White Mountains resident planning a European hiking trip, a Mount Washington Valley family heading to Canada, or a Conway-area business owner with international connections.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Conway%20New%20Hampshire%20downtown%20with%20historic%20brick%20buildings%20along%20Main%20Street%20and%20the%20Conway%20Scenic%20Railroad%20station%20under%20warm%20summer%20afternoon%20light%2C%20White%20Mountains%20and%20Mount%20Washington%20visible%20in%20background%2C%20classic%20New%20England%20small%20town%20architecture%20with%20scenic%20mountain%20valley%20setting%2C%20wide%20angle%20professional%20photography%20with%20crisp%20clean%20composition&width=1800&height=700&seq=conway-nh-hero-001&orientation=landscape",
  heroImageAlt: "Conway New Hampshire downtown with Scenic Railroad station and White Mountains backdrop",
  geoPosition: "43.9792;-71.1203",
  geoPlacename: "Conway, New Hampshire",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "Tip O'Neill Federal Building, 10 Causeway St, Boston, MA 02222",
    distance: "Approximately 130 miles from Conway — about 2 hours 15 minutes via NH-16/I-95",
  },
  nearbyCitiesZips: [
    { name: "Conway", zips: "03818" },
    { name: "North Conway", zips: "03860" },
    { name: "Bartlett", zips: "03812" },
    { name: "Madison", zips: "03849" },
    { name: "Freedom", zips: "03836" },
    { name: "Tamworth", zips: "03886" },
    { name: "Ossipee", zips: "03864" },
    { name: "Jackson", zips: "03846" },
    { name: "Glen", zips: "03838" },
    { name: "Intervale", zips: "03845" },
    { name: "Eaton", zips: "03832" },
    { name: "Albany", zips: "03818" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Boston Passport Agency — about 130 miles from Conway via NH-16" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "White Mountains Hub", desc: "Serving Conway, North Conway, Bartlett, Jackson, and all Carroll County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Conway case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Boston MA", route: "/cities/boston" },
    { name: "Expedited passport in Manchester NH", route: "/cities/manchester" },
    { name: "Urgent passport in Conway NH", route: "/cities/conway-nh" },
    { name: "New Hampshire passport hub", route: "/states/new-hampshire" },
    { name: "Fast passport in Portsmouth NH", route: "/cities/portsmouth" },
    { name: "Expedited passport in Concord NH", route: "/cities/concord-nh" },
    { name: "Emergency passport in Nashua NH", route: "/cities/nashua" },
  ],
  fpcOfficeAddress: "Tip O'Neill Federal Building, 10 Causeway St, Boston, MA 02222",
  fpcOfficeNote: "Fast Passport Center serves Conway remotely via secure courier. Our registered couriers hand-deliver your documents to the Boston Passport Agency — approximately 130 miles south via NH-16 and I-95. Conway's location in the Mount Washington Valley means travelers never need to make the 2-hour-plus drive to Boston for passport processing. Our courier service handles everything while you focus on preparing for your trip.",
  fpcOfficeDistance: "Serving all of Mount Washington Valley and Carroll County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Conway, North Conway, Bartlett, Jackson, and the Mount Washington Valley. From White Mountains outdoor enthusiasts heading abroad to Carroll County families visiting Canada, we help Mount Washington Valley travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Conway and nearby communities including North Conway, Bartlett, Madison, Freedom, Tamworth, Ossipee, Jackson, Glen, Intervale, and all of Carroll County.",
  howItWorksIntro: "Most Conway travelers complete everything online and ship documents overnight. At approximately 130 miles from Boston, Fast Passport Center provides fast courier turnaround for Mount Washington Valley residents — eliminating the 2-hour-plus drive to the Boston Passport Agency.",
  mistakesIntro: "Conway travelers, including White Mountains outdoor enthusiasts, Mount Washington Valley resort workers, and Carroll County families, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Conway and Mount Washington Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Conway? Here are the most common ones we hear from Mount Washington Valley and Carroll County travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  dmvWebsite: "https://www.dmv.nh.gov/",
  dmvLabel: "New Hampshire DMV — Official Website",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "New Hampshire Vital Records (Birth, Death, Marriage)", href: "https://www.sos.nh.gov/records-and-vital-records" },
    { label: "Carroll County Vital Records", href: "https://www.carrollcountynh.net/" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins in Boston",
        "Life-or-death emergencies only",
        "130 miles to Boston (2 hours 15 minutes via NH-16/I-95)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for White Mountains international travel",
        "Perfect for Mount Washington Valley family trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PWM or MHT",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Conway is 130 miles from the Boston Passport Agency — our courier service saves Mount Washington Valley travelers the drive.",
      bullets: ["Mount Washington Valley coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in White Mountains",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Conway adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Conway and Carroll County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Conway and Carroll County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Conway and Mount Washington Valley residents with DS-64 form guidance and courier routing to Boston.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Boston agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Carroll County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Conway and Mount Washington Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Conway NH", description: "Urgent passport processing for Conway, NH travelers via Boston Passport Agency courier — 130 miles from Conway." },
    { name: "Passport Renewal Conway NH", description: "Fast passport renewal for Conway and Carroll County, New Hampshire residents." },
    { name: "New Passport Conway NH", description: "First-time passport application assistance for Conway and Mount Washington Valley area residents." },
    { name: "Lost Passport Conway NH", description: "Passport replacement for lost or stolen passports for Conway and Carroll County residents." },
    { name: "Child Passport Conway NH", description: "Minor passport services for Conway and Carroll County families." },
    { name: "Name Change Passport Conway NH", description: "Passport name change service for Conway, NH residents." },
  ],
  testimonials: [
    {
      name: "Mike D.",
      location: "North Conway, NH",
      rating: 5,
      text: "Outdoor guide leading a hiking expedition to the Swiss Alps. Two weeks before departure I realized my passport had expired. Fast Passport Center had me a new one in 4 business days. The Boston courier service was seamless and my agent was incredible.",
      service: "Emergency Renewal",
    },
    {
      name: "Patricia & Bob L.",
      location: "Conway, NH",
      rating: 5,
      text: "Retirees living in Conway who planned a river cruise through Germany. Three weeks before departure we discovered Bob's passport had expired. Fast Passport Center renewed it in 5 business days. We made our cruise and had the trip of a lifetime.",
      service: "Expedited Renewal",
    },
    {
      name: "Sarah C.",
      location: "Bartlett, NH",
      rating: 5,
      text: "Ski instructor at Attitash who got a last-minute opportunity to work at a resort in New Zealand for their winter season. Needed a new passport fast. Fast Passport Center processed my application and I had my passport in 6 business days. The courier service saved me a 4-hour round trip to Boston.",
      service: "New Passport",
    },
    {
      name: "The R Family",
      location: "Jackson, NH",
      rating: 5,
      text: "Family of four planning a summer trip to Canada's Maritime provinces. Two adult renewals and two child passports needed. Fast Passport Center handled everything — all four passports arrived in 8 business days. The kids were so excited for their first international trip.",
      service: "Family Applications",
    },
  ],
  facilities: [
    {
      id: "cv-1",
      name: "Conway Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1766 E Main St",
      city: "Center Conway",
      state: "NH",
      zip: "03813",
      phone: "(603) 447-3821",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Conway — Center Conway",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Conway and Mount Washington Valley residents. Appointment required. Located on East Main Street in Center Conway.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-2",
      name: "North Conway Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "78 Main St",
      city: "North Conway",
      state: "NH",
      zip: "03860",
      phone: "(603) 356-5433",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North Conway — 2 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for North Conway village residents and outlet shoppers. Downtown location on Main Street. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-3",
      name: "Carroll County Clerk Office",
      type: "County Clerk Passport Acceptance",
      address: "95 Water Village Rd",
      city: "Ossipee",
      state: "NH",
      zip: "03864",
      phone: "(603) 539-7772",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Ossipee — 15 miles south",
      website: "https://www.carrollcountynh.net",
      websiteLabel: "Carroll County Government",
      notes: "County-level passport acceptance services. Convenient for southern Carroll County residents. Call ahead to confirm passport service hours.",
      icon: "ri-government-line",
    },
    {
      id: "cv-4",
      name: "Tamworth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1191 Main St",
      city: "Tamworth",
      state: "NH",
      zip: "03886",
      phone: "(603) 323-8581",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Tamworth — 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small-town option for southern Carroll County residents. Call ahead — limited hours may vary.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-5",
      name: "Fryeburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "38 Portland St",
      city: "Fryeburg",
      state: "ME",
      zip: "04037",
      phone: "(207) 935-2143",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Fryeburg, ME — 10 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Maine option just across the state line. Convenient for eastern Carroll County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-6",
      name: "Ossipee Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 Old Route 28",
      city: "Ossipee",
      state: "NH",
      zip: "03864",
      phone: "(603) 539-6883",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Ossipee — 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Carroll County and Ossipee Lake area residents. Near the county government complex.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Boston Agency", agency: "130 miles — 2 hour 15 minute drive via NH-16/I-95, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming the National Passport Center in Portsmouth Handles Walk-Ins",
      description: "New Hampshire residents sometimes confuse the National Passport Center in Portsmouth with a passport agency. The Portsmouth facility is a processing center only — it does not accept public visits or walk-in applications. The closest actual passport agency for emergency service is in Boston, 130 miles from Conway.",
    },
    {
      icon: "ri-calendar-line",
      title: "Ski Season Workers Not Planning Passports Around Seasonal Employment",
      description: "Mount Washington Valley ski resort workers who get seasonal job offers at resorts in New Zealand, Chile, or Europe often have just 2–4 weeks between seasons. Waiting until after the ski season ends to handle passport applications leaves almost no time for processing — especially during peak summer travel season.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking From PWM or MHT",
      description: "Carroll County travelers often book international flights from Portland Jetport (PWM) or Manchester-Boston Regional (MHT) months in advance without checking passport expiration dates. Most countries require 6 months passport validity beyond your return date.",
    },
    {
      icon: "ri-global-line",
      title: "Confusing Enhanced Driver's License With Passport Requirements",
      description: "Some New Hampshire residents have Enhanced Driver's Licenses (EDLs) which allow land and sea travel to Canada, Mexico, and some Caribbean destinations. However, EDLs cannot be used for international air travel. You need a valid U.S. passport for any international flight.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewing by Mail When In-Person Is Required",
      description: "Conway residents with passports that expired more than 5 years ago, were issued under age 16, or have a name change without documentation must use Form DS-11 (in-person application) — not DS-82 (mail-in). Filing the wrong form means your application gets rejected.",
    },
    {
      icon: "ri-image-line",
      title: "Using Ski Resort ID Photos as Passport Photos",
      description: "Mount Washington Valley seasonal workers sometimes try to use their resort ID photos or self-taken photos for passport applications. Passport photos must be exactly 2x2 inches with a pure white background and neutral expression — only professional passport photo services meet the strict U.S. Department of State standards.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Conway, North Conway, or Fryeburg." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Boston Passport Agency — 130 miles south via NH-16." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Boston Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Conway or Mount Washington Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Conway from the Boston passport agency?",
      a: "Conway is approximately 130 miles from the Boston Passport Agency at the Tip O'Neill Federal Building, 10 Causeway St — about a 2 hour 15 minute drive south via NH-16 and I-95. However, the Boston agency requires pre-scheduled NPIC appointments and does not accept walk-ins.",
    },
    {
      q: "Does Conway, NH have its own passport agency?",
      a: "No. Conway does not have a passport agency. The closest passport agency is the Boston Passport Agency, about 130 miles south. Conway only has passport acceptance facilities — the Conway Post Office and North Conway Post Office — which can accept DS-11 applications but cannot process or expedite them.",
    },
    {
      q: "What about the National Passport Center in Portsmouth — is that closer?",
      a: "The National Passport Center in Portsmouth, NH is a Department of State processing center — not a public passport agency. It does not accept walk-in visitors or provide any in-person services to the public. The closest passport agency for emergency service remains the Boston Passport Agency, 130 miles from Conway.",
    },
    {
      q: "Where do Conway travelers typically fly internationally?",
      a: "Conway travelers typically fly from Portland International Jetport (PWM, about 65 miles southeast), Manchester-Boston Regional Airport (MHT, about 85 miles south), or Boston Logan International Airport (BOS, about 130 miles south) for international flights.",
    },
    {
      q: "How fast can I get an emergency passport in Conway?",
      a: "Emergency processing is typically 24–48 hours for Conway residents. Given Conway's location on major NH-16 corridor with good access to overnight shipping routes through Portland and Manchester, courier routing to Boston is reliable. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Where can I get passport photos in Conway?",
      a: "Walgreens at 1585 White Mountain Hwy in North Conway and CVS Pharmacy at 1376 White Mountain Hwy in North Conway both offer passport photo services. The UPS Store at 1496 White Mountain Hwy in North Conway also provides photos. Always verify your photos meet exact 2x2 inch specifications with a white background.",
    },
    {
      q: "I'm a seasonal worker in the Mount Washington Valley — can you help with an international job?",
      a: "Absolutely. We regularly help Mount Washington Valley seasonal workers — ski instructors, outdoor guides, and resort staff — who get seasonal job offers at international resorts. Emergency processing delivers passports in as fast as 24–48 hours. Start the process as soon as you accept your international position.",
    },
    {
      q: "How do I renew my passport in Conway?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If not eligible for mail-in renewal, visit the Conway Post Office for an in-person DS-11 application.",
    },
    {
      q: "Does the Conway Post Office take walk-in passport appointments?",
      a: "The Conway Post Office at 1766 E Main St in Center Conway accepts passport applications by appointment Monday through Friday from 9:00 AM to 4:00 PM. Walk-ins are generally not accepted — call (603) 447-3821 to schedule. Summer and fall foliage seasons are busiest.",
    },
    {
      q: "Can I use my New Hampshire Enhanced Driver's License instead of a passport?",
      a: "A New Hampshire Enhanced Driver's License (EDL) allows land and sea travel to Canada, Mexico, and some Caribbean countries, but it cannot be used for international air travel. You must have a valid U.S. passport to fly to any international destination.",
    },
    {
      q: "Can Fast Passport Center help Bartlett and Jackson residents too?",
      a: "Absolutely. We serve all of the Mount Washington Valley including Conway, North Conway, Bartlett, Jackson, Glen, Intervale, Madison, Freedom, Tamworth, Ossipee, and Eaton. Residents of surrounding communities never need to drive to Conway or Boston for service — our courier handles everything.",
    },
    {
      q: "Is the North Conway Post Office faster than the Conway Post Office for passport applications?",
      a: "Both the North Conway Post Office and the Conway (Center Conway) Post Office serve as passport acceptance facilities with identical processing capabilities. Neither facility can expedite processing — they only accept applications for standard mail-in processing. That's where Fast Passport Center's courier service comes in, providing the only expedited option for Mount Washington Valley residents.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — North Conway", address: "1585 White Mountain Hwy, North Conway, NH 03860", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/photo" },
    { name: "CVS Pharmacy — North Conway", address: "1376 White Mountain Hwy, North Conway, NH 03860", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/photo" },
    { name: "UPS Store — North Conway", address: "1496 White Mountain Hwy, North Conway, NH 03860", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart Photo — North Conway", address: "46 Mountain Valley Blvd, North Conway, NH 03860", hours: "Mon–Sun: 7AM–9PM", website: "https://www.walmart.com/photo" },
    { name: "Walgreens — Fryeburg", address: "584 Main St, Fryeburg, ME 04037", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — Westbrook", address: "5 Hannaford Dr, Westbrook, ME 04092", hours: "Mon–Fri: 7:30AM–7PM, Sat: 9AM–5PM", note: "Closest FedEx Office for printing and shipping passport documents. About 55 miles southeast near Portland.", website: "https://www.office.fedex.com/" },
  ],
  dmvLocations: [
    { name: "NH DMV — Tamworth", address: "1864 White Mountain Hwy, Tamworth, NH 03886", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Closest NH DMV to Conway. REAL ID, driver's license, and state ID services for Carroll County and Mount Washington Valley residents.", featured: true },
    { name: "NH DMV — Conway (Mobile Unit)", address: "Carroll County Complex, 95 Water Village Rd, Ossipee, NH 03864", hours: "Select days — call ahead", note: "Mobile DMV unit serving Carroll County. Check schedule before visiting as days and hours vary seasonally.", featured: false },
    { name: "NH DMV — Rochester", address: "38 Route 11, Farmington, NH 03835", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Full-service DMV location. Serves Strafford County and southern Carroll County. About 50 miles south of Conway.", featured: false },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "65 miles southeast of Conway",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Closest commercial airport serving Conway with domestic routes and connections to international hubs. About 1 hour 15 minutes via US-302. Most convenient for Mount Washington Valley travelers.",
      flightUrl: "https://portlandjetport.org/",
      flightLabel: "PWM Airport",
      featured: true,
    },
    {
      code: "MHT",
      name: "Manchester-Boston Regional Airport",
      distance: "85 miles south of Conway",
      address: "1 Airport Rd, Manchester, NH 03103",
      note: "New Hampshire's busiest airport with extensive domestic routes. About 1 hour 45 minutes via NH-16/I-93. Alternative for southern Carroll County residents.",
      flightUrl: "https://www.flymanchester.com/",
      flightLabel: "MHT Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Boston Logan International Airport",
      distance: "130 miles south of Conway",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights worldwide. About 2 hours 15 minutes via NH-16/I-95. Best option for direct international flights. Co-located near Boston Passport Agency.",
      flightUrl: "https://www.massport.com/logan-airport/",
      flightLabel: "BOS Airport",
      featured: false,
    },
    {
      code: "LEB",
      name: "Lebanon Municipal Airport",
      distance: "75 miles southwest of Conway",
      address: "5 Airpark Rd, Lebanon, NH 03784",
      note: "Regional airport with limited commercial service via Cape Air to Boston and White Plains. Primarily for connecting flights. About 1 hour 30 minutes via NH-25.",
      flightUrl: "https://flyleb.com/",
      flightLabel: "LEB Airport",
      featured: false,
    },
  ],
};